function mapAlphaNumericToUnicode(
  input: string,
  upperStart: number,
  lowerStart: number,
  digitStart?: number
): string {
  let output = "";
  for (const character of input) {
    const codePoint = character.codePointAt(0);
    if (!codePoint) {
      output += character;
      continue;
    }

    if (codePoint >= 65 && codePoint <= 90) {
      output += String.fromCodePoint(upperStart + (codePoint - 65));
      continue;
    }

    if (codePoint >= 97 && codePoint <= 122) {
      output += String.fromCodePoint(lowerStart + (codePoint - 97));
      continue;
    }

    if (digitStart !== undefined && codePoint >= 48 && codePoint <= 57) {
      output += String.fromCodePoint(digitStart + (codePoint - 48));
      continue;
    }

    output += character;
  }
  return output;
}

type UnicodeAlphaNumericMap = {
  upperStart: number;
  lowerStart: number;
  digitStart?: number;
};

function decodeAlphaNumericFromMap(
  codePoint: number,
  map: UnicodeAlphaNumericMap
): number | null {
  if (codePoint >= map.upperStart && codePoint <= map.upperStart + 25) {
    return 65 + (codePoint - map.upperStart);
  }
  if (codePoint >= map.lowerStart && codePoint <= map.lowerStart + 25) {
    return 97 + (codePoint - map.lowerStart);
  }
  if (
    map.digitStart !== undefined &&
    codePoint >= map.digitStart &&
    codePoint <= map.digitStart + 9
  ) {
    return 48 + (codePoint - map.digitStart);
  }
  return null;
}

function mapUnicodeVariantsToAscii(
  input: string,
  maps: UnicodeAlphaNumericMap[]
): string {
  let output = "";

  for (const character of input) {
    const codePoint = character.codePointAt(0);
    if (!codePoint) {
      output += character;
      continue;
    }

    let decoded: number | null = null;
    for (const map of maps) {
      decoded = decodeAlphaNumericFromMap(codePoint, map);
      if (decoded !== null) break;
    }

    output += decoded !== null ? String.fromCodePoint(decoded) : character;
  }

  return output;
}

const BOLD_UNICODE_MAPS: UnicodeAlphaNumericMap[] = [
  // Legacy mathematical bold
  { upperStart: 0x1d400, lowerStart: 0x1d41a, digitStart: 0x1d7ce },
  // Current sans-serif bold
  { upperStart: 0x1d5d4, lowerStart: 0x1d5ee, digitStart: 0x1d7ec },
];

const ITALIC_UNICODE_MAPS: UnicodeAlphaNumericMap[] = [
  // Legacy mathematical italic
  { upperStart: 0x1d434, lowerStart: 0x1d44e },
  // Current sans-serif italic
  { upperStart: 0x1d608, lowerStart: 0x1d622 },
];

function isAsciiAlphaNumeric(codePoint: number): boolean {
  return (
    (codePoint >= 65 && codePoint <= 90) ||
    (codePoint >= 97 && codePoint <= 122) ||
    (codePoint >= 48 && codePoint <= 57)
  );
}

function isFullyStyledWithMaps(
  input: string,
  maps: UnicodeAlphaNumericMap[],
  extraStyledCodePoints: number[] = []
): boolean {
  let hasTransformableCharacter = false;

  for (const character of input) {
    const codePoint = character.codePointAt(0);
    if (!codePoint) continue;

    if (isAsciiAlphaNumeric(codePoint)) {
      hasTransformableCharacter = true;
      return false;
    }

    if (extraStyledCodePoints.includes(codePoint)) {
      hasTransformableCharacter = true;
      continue;
    }

    const isStyled = maps.some(
      (map) => decodeAlphaNumericFromMap(codePoint, map) !== null
    );
    if (isStyled) {
      hasTransformableCharacter = true;
    }
  }

  return hasTransformableCharacter;
}

function addCombiningMark(input: string, mark: string): string {
  let output = "";
  for (const character of input) {
    if (character === "\n" || character === " ") {
      output += character;
      continue;
    }
    output += `${character}${mark}`;
  }
  return output;
}

export function toUnicodeBold(input: string): string {
  // Use sans-serif bold Unicode so styled text stays visually closer to the base font.
  return mapAlphaNumericToUnicode(input, 0x1d5d4, 0x1d5ee, 0x1d7ec);
}

export function toUnicodeItalic(input: string): string {
  // Use sans-serif italic Unicode for a more consistent visual style.
  return mapAlphaNumericToUnicode(input, 0x1d608, 0x1d622);
}

export function fromUnicodeBold(input: string): string {
  return mapUnicodeVariantsToAscii(input, BOLD_UNICODE_MAPS);
}

export function fromUnicodeItalic(input: string): string {
  return mapUnicodeVariantsToAscii(input, ITALIC_UNICODE_MAPS).replace(
    /\u210E/g,
    "h"
  );
}

export function toUnicodeUnderline(input: string): string {
  return addCombiningMark(input, "\u0332");
}

export function toUnicodeStrikethrough(input: string): string {
  return addCombiningMark(input, "\u0336");
}

export function toggleUnicodeBold(input: string): string {
  if (isFullyStyledWithMaps(input, BOLD_UNICODE_MAPS)) {
    return fromUnicodeBold(input);
  }
  return toUnicodeBold(fromUnicodeBold(input));
}

export function toggleUnicodeItalic(input: string): string {
  if (isFullyStyledWithMaps(input, ITALIC_UNICODE_MAPS, [0x210e])) {
    return fromUnicodeItalic(input);
  }
  return toUnicodeItalic(fromUnicodeItalic(input));
}

export function toggleUnicodeUnderline(input: string): string {
  return input.includes("\u0332")
    ? input.split("\u0332").join("")
    : toUnicodeUnderline(input);
}

export function toggleUnicodeStrikethrough(input: string): string {
  return input.includes("\u0336")
    ? input.split("\u0336").join("")
    : toUnicodeStrikethrough(input);
}
